import { Link } from "react-router-dom";
import { ArrowUpRight, Boxes, PencilRuler, Tag, Cog, Ship } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Reveal, MaskedLines, FadeIn } from "../components/motion/Reveal";
import EditorialMarquee from "../components/EditorialMarquee";
import PageMeta from "../components/PageMeta";
import { IMAGES, pimg } from "../lib/site";

const OFFERINGS = [
  { icon: Boxes, title: "Wholesale", points: ["Bulk Orders", "Wholesale Pricing"], desc: "Tiered pricing that improves with volume. Stock our proven catalog lines under standard or custom branding." },
  { icon: PencilRuler, title: "Custom Manufacturing", points: ["Custom Product", "Custom Colors", "Custom Sizes", "Custom Branding"], desc: "Bring a spec sheet or a sketch — our engineers turn it into a production-ready product with sampled approval." },
  { icon: Tag, title: "Private Label", points: ["Logo", "Packaging", "Branding"], desc: "Your brand on our proven constructions. Logo application, retail packaging, hang tags, and barcodes included." },
  { icon: Cog, title: "OEM / ODM", points: ["Product Development", "Samples", "Manufacturing"], desc: "Full development cycle: concept, tooling, sampling, compliance testing, and scaled manufacturing." },
  { icon: Ship, title: "Export", points: ["International Orders", "Shipping", "Documentation"], desc: "FOB, CIF, or DDP to 40+ countries. We handle export declarations, certificates, and freight coordination." },
];

const B2B = () => (
  <div data-testid="b2b-page">
    <PageMeta
      title="B2B & Private Label Manufacturing Programs"
      description="Turnkey OEM and ODM pickleball manufacturing programs for sports brands, distributors, and clubs. Custom molding, USAPA specs, and global shipping."
    />
    <section className="relative overflow-hidden border-b border-black/10 bg-brand-ink text-white">
      <div className="blueprint-grid-dark pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-5 pb-24 pt-16 md:pt-24 lg:px-10">
        <FadeIn>
          <p className="mono-caption mb-8 text-white/50">B2B — Wholesale & Custom Programs</p>
        </FadeIn>
        <h1 className="max-w-4xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
          <MaskedLines lines={["YOUR SUPPLY CHAIN", <>STARTS HERE<span className="text-brand-red">.</span></>]} />
        </h1>
        <FadeIn delay={0.6}>
          <p className="mt-8 max-w-xl leading-relaxed text-white/60">
            From 100-piece private label runs to container-scale OEM programs — we build the
            supply chain behind your pickleball brand.
          </p>
          <Link to="/inquiry?type=bulk" className="btn-primary mt-10" data-testid="b2b-hero-quote">
            Request a Quote <ArrowUpRight size={16} />
          </Link>
        </FadeIn>
      </div>
    </section>

    <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
      <SectionHeading number="01" title="What We Offer" sub="Five programs. One factory. Zero middlemen." />
      <div className="grid gap-px border border-black/10 bg-black/10 md:grid-cols-2 lg:grid-cols-3">
        {OFFERINGS.map((o, i) => (
          <Reveal key={o.title} delay={i * 0.05}>
            <div className="group flex h-full flex-col bg-white p-8 transition-colors hover:bg-brand-ink" data-testid={`b2b-offer-${i}`}>
              <o.icon size={26} className="text-brand-red" />
              <h3 className="mt-6 font-display text-2xl font-medium tracking-tight group-hover:text-white">{o.title}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {o.points.map((pt) => (
                  <span key={pt} className="border border-black/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-neutral-500 group-hover:border-white/20 group-hover:text-white/60">
                    {pt}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-neutral-500 group-hover:text-white/60">{o.desc}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.25}>
          <Link to="/inquiry?type=bulk" className="flex h-full min-h-56 flex-col justify-between bg-brand-red p-8 text-white transition-colors hover:bg-brand-reddark" data-testid="b2b-offer-cta">
            <p className="font-display text-2xl font-medium tracking-tight">Tell us what you need. Get a quote in 24h.</p>
            <ArrowUpRight size={28} />
          </Link>
        </Reveal>
      </div>
    </section>

    <EditorialMarquee />

    <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border-2 border-brand-red" />
            <img src={pimg(IMAGES.factory, 1000, 800)} alt="Manufacturing facility" className="relative aspect-[5/4] w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="font-display text-3xl font-medium tracking-tight md:text-4xl">
            Sample in 10 days. Production in 30<span className="text-brand-red">.</span>
          </h2>
          <p className="mt-6 leading-relaxed text-neutral-500">
            Speed is a feature. Our in-house tooling and print lines mean samples ship in 7–10 days
            and approved orders enter production within 48 hours of sign-off. Dedicated account
            managers keep you updated at every stage.
          </p>
          <ul className="mt-8 space-y-3 font-mono text-sm">
            {["7–10 day sampling", "48h production kickoff after approval", "In-line QC with photo reports", "Real-time shipment tracking"].map((t) => (
              <li key={t} className="flex items-center gap-3 border-b border-black/10 pb-3">
                <span className="h-1.5 w-1.5 bg-brand-red" /> {t}
              </li>
            ))}
          </ul>
          <Link to="/inquiry?type=private-label" className="btn-dark mt-10" data-testid="b2b-private-label-cta">
            Start a Private Label Program
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default B2B;
